import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentIcon,
  PlusIcon,
  TicketIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import StaffShell from '../StaffShell';
import staffApi, { apiError } from '../api';
import { useAuth } from '../AuthContext';

function InvitePanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const load = () => {
    staffApi.get('/auth/invite-codes/')
      .then((res) => setCodes(res.data))
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      await staffApi.post('/auth/invite-codes/', { role: 'technician' });
      load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="rounded-2xl border border-line bg-surface p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <TicketIcon className="h-5 w-5 text-lime" />
          <h2 className="text-ink font-bold text-lg">Invite a technician</h2>
        </div>
        <button onClick={generate} disabled={generating}
          className="inline-flex items-center gap-1.5 rounded-full bg-lime text-navy font-bold text-sm px-4 py-2 hover:bg-green-400 transition disabled:opacity-60">
          <PlusIcon className="h-4 w-4" /> {generating ? 'Generating…' : 'Generate code'}
        </button>
      </div>
      <p className="text-ink-soft text-sm mb-4">
        Share a single-use code with a new technician — they enter it when creating their account.
      </p>
      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
      {loading ? (
        <p className="text-ink-soft text-sm">Loading codes…</p>
      ) : codes.length === 0 ? (
        <p className="text-ink-soft text-sm">No codes yet — generate one above.</p>
      ) : (
        <ul className="divide-y divide-line">
          {codes.map((c) => (
            <li key={c.code} className="flex items-center justify-between py-2.5">
              <span className={`font-mono tracking-widest font-bold ${c.used_by ? 'text-ink-soft line-through' : 'text-ink'}`}>
                {c.code}
              </span>
              {c.used_by ? (
                <span className="text-xs font-semibold text-ink-soft">used</span>
              ) : (
                <button onClick={() => copy(c.code)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-lime hover:text-green-400">
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copied === c.code ? 'Copied!' : 'Copy'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, to }) {
  return (
    <Link to={to} className="rounded-2xl border border-line bg-surface p-6 flex flex-col hover:border-lime/60 hover:shadow-xl transition">
      <Icon className="h-7 w-7 text-lime mb-3" />
      <h3 className="text-ink font-bold text-lg">{title}</h3>
      <p className="text-ink-soft text-sm mt-1 flex-1">{desc}</p>
    </Link>
  );
}

export default function StaffHome() {
  const { user, isOffice } = useAuth();

  return (
    <StaffShell max="max-w-5xl">
      <p className="text-lime text-[11px] font-bold uppercase tracking-[0.25em] mb-1">
        {isOffice ? 'Office' : 'Technician'} workspace
      </p>
      <h1 className="text-white text-3xl font-extrabold mb-8">
        {isOffice ? 'Supervisor dashboard' : `Welcome, ${user?.first_name || user?.username}`}
      </h1>

      {isOffice ? (
        <>
          <div className="grid gap-5 md:grid-cols-3 mb-5">
            <ActionCard icon={DocumentTextIcon} title="All reports" to="/staff/app/office/reports"
              desc="Browse and filter every technician's reports; open one to review it." />
            <ActionCard icon={ChartBarIcon} title="Dashboard" to="/staff/app/office/dashboard"
              desc="Daily jobs, installs, repairs, materials used and monthly technician performance." />
            <ActionCard icon={CubeIcon} title="Inventory" to="/staff/app/office/inventory"
              desc="Current stock per material; negative stock is flagged." />
          </div>
          <InvitePanel />
        </>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          <ActionCard icon={DocumentPlusIcon} title="New job report" to="/staff/app/report/new"
            desc="File a report for each job you visit — customer, work done, materials, status and photos." />
          <ActionCard icon={DocumentTextIcon} title="My reports" to="/staff/app/reports"
            desc="See every report you've submitted and its review status." />
        </div>
      )}
    </StaffShell>
  );
}
